import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Document, Page, Text, View, Image, Font } from '@react-pdf/renderer';
import { PdfPreviewWithTools } from '../../../PdfPreviewWithTools';
import { squadPageLayoutWarning, summarizeSquadPageLayout, type SquadPageLayout } from '../squadPageLayout';
import {
  loadSquadImageSize,
  resolveSquadPage3Layout,
  squadPage3ImageWarning,
  type SquadImageSize,
  type SquadPage3ImageArea,
} from '../squadPage3Image';
import {
  buildFactsheetDownloadFileName,
  resolveFactsheetDownloadAsOf,
} from '../../../../../../utils/documentFileName';
import { registerReactPdfGermanSafeWrap } from '../../reactPdfHyphenation';
import { getPdfStyles } from './styles';
import { ensureSquadTitleMeasureFont, fitSquadTitleFontSize } from '../squadTitleFit';
import { SquadPdfPageHeader } from '../SquadPdfPageHeader';
import {
  SQUAD_NAV_CHART_HEIGHT_PDF_PT,
  SQUAD_ROLLING_CHART_HEIGHT_PDF_PT,
} from '../squadPortfolioChartUtils';
import { buildSquadFondspartnerBlocks } from '../squadFondspartner';
import { parseSquadFootnotes, squadSidebarFootnoteKey, type SquadSidebarFootnotePage } from '../squadFootnotes';
import { squadLabeledKeys } from '../squadLabels';
import {
  SQUAD_COLORS,
  SQUAD_FONDSDATEN_KEYS,
  SQUAD_WEITERE_DATEN_KEYS,
  SQUAD_FONDSPARTNER_KEYS,
  IMAGE_SLOT_LABELS,
  SQUAD_FONT_FILES,
  SQUAD_PDF_FONT_FAMILY,
  SQUAD_GEBUEHREN_LABEL_WIDTH_PCT,
} from '../templateConstants';
import {
  expandSquadSectionHeaders,
  resolveSquadHeaderAsOf,
  squadPageMetaLabel,
} from '../sectionHeaderPlaceholders';
import { SQUAD_TYPOGRAPHY_PDF } from '../pageHeaderTokens';
import {
  KeyValueBlock,
  Placeholder,
  KalenderjahrTable,
  PerformanceRiskTable,
  RiskIndicatorBlock,
  BulletList,
  SquadPortfolioSectionsPdf,
  PageLayoutProbe,
  FootnoteLines,
  InlineProseText,
  MifidOverlayLayerPdf,
  SquadPage3ImagePdf,
} from '../squadPdfParts';
import { isSquadRasterPie } from '../squadVectorDonut';
import { SquadAdvisorsPdf } from '../SquadAdvisorsSection';
import { RichHtmlPdf } from '../squadRichText';
import { performanceRiskTableForRender } from '../performanceRiskTable';
import { normalizeMifidOverlay } from '../squadMifidOverlay';
import { resolveSquadFsHeaderPalette } from '../squadCustomPortfolio';
import { fetchImageForPdf } from '../../69842241581723c403c697fc/s3PdfImage';
import { parseSquadSrriLevel } from '../squadSrriwert';
import { resolveSquadGebuehren } from '../squadGebuehrenDynamic';
import { resolveSquadFondsdatenRows } from '../squadFondsdatenDynamic';
import { mapSquadKvRows } from '../squadKvRows';
import {
  fetchSquadDynamicBundle,
  squadMissingDynamicWarning,
  type SquadDynamicBundle,
} from '../fetchSquadDynamicBundle';
import {
  normalizeSquadAdvisors,
  SQUAD_ADVISOR_FOOTER_LOGO,
  SquadAdvisorRecord,
} from '../squadAdvisors';
/** react-pdf does not reliably render WebP — rasterize to PNG data URL. */
async function ensurePdfSafeImageDataUrl(dataUrl: string): Promise<string> {
  if (!dataUrl.startsWith('data:image/webp')) return dataUrl;
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      try {
        const canvas = window.document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(dataUrl);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

async function resolveSlotImageForPdf(url?: string): Promise<string | undefined> {
  const raw = typeof url === 'string' ? url.trim() : '';
  if (!raw) return undefined;
  if (raw.startsWith('data:')) return ensurePdfSafeImageDataUrl(raw);
  const fetched = await fetchImageForPdf(raw);
  if (!fetched) return undefined;
  return ensurePdfSafeImageDataUrl(fetched);
}

// Allow long German compounds to wrap in narrow sidebar KV cells without
// inserting a visible hyphen or orphaning trailing punctuation (e.g. "ausgetauscht.").
registerReactPdfGermanSafeWrap(14);

let squadPdfFontsRegistered = false;
if (!squadPdfFontsRegistered) {
  try {
    Font.register({
      family: SQUAD_PDF_FONT_FAMILY,
      fonts: [
        { src: SQUAD_FONT_FILES.regular, fontWeight: 400 },
        // Required: titleSquad/titleRest use fontWeight 600 — missing weight blanks the PDF.
        { src: SQUAD_FONT_FILES.semibold, fontWeight: 600 },
        { src: SQUAD_FONT_FILES.bold, fontWeight: 700 },
        { src: SQUAD_FONT_FILES.italic, fontWeight: 400, fontStyle: 'italic' },
        { src: SQUAD_FONT_FILES.boldItalic, fontWeight: 700, fontStyle: 'italic' },
      ],
    });
    squadPdfFontsRegistered = true;
  } catch {
    // ignore duplicate registration
  }
}

interface FactsheetPDFProps {
  data: any;
  template?: any;
  chartImageDataUrl?: string;
  chart1ImageDataUrl?: string;
  pieChart1ImageDataUrl?: string;
  pieChart2ImageDataUrl?: string;
  /** Pre-fetched data URLs (S3 → blob) so react-pdf can embed slot images. */
  historischeAssetAllokationImageSrc?: string;
  investmentprozessImageSrc?: string;
  zusammenfassungImageSrc?: string;
  /** Page 3 below Rollierend: the one image replacing the three above — see squadPage3Image. */
  page3ImageSrc?: string;
  ratingsImageSrc?: string;
  /** Advisors with photo/logo resolved to data URLs for react-pdf. */
  advisorsForPdf?: SquadAdvisorRecord[];
  /** Optionales 1280×540-Logo am Fuss der Seite-2-Seitenleiste, bereits als Daten-URI. */
  fondspartnerLogoSrc?: string;
  advisorFooterLogoSrc?: string;
  /** Live Axion values for dynamic Fondsdaten money fields. */
  fondsdatenCache?: Map<string, string>;
  /** Live values for dynamic section headers (e.g. cixon_chart.datum). */
  sectionHeaderCache?: Map<string, string>;
  /** Nur Vorschaudialog: meldet je logischer Seite die physischen Seiten (Überlauf-Warnung). */
  onPageLayout?: (logicalPage: number, pageNumber: number, totalPages: number) => void;
  /** Preview dialog only: the laid-out size of the page-3 image area (pt), for the too-tall warning. */
  onPage3ImageArea?: (widthPt: number, heightPt: number) => void;
}

const asKv = (map: Record<string, string> | undefined, keys: Array<{ key: string; label: string }>) =>
  mapSquadKvRows(map, keys);

export const FactsheetPDF: FC<FactsheetPDFProps> = ({
  data,
  template: templateProp,
  chartImageDataUrl,
  chart1ImageDataUrl,
  pieChart1ImageDataUrl,
  pieChart2ImageDataUrl,
  historischeAssetAllokationImageSrc,
  investmentprozessImageSrc,
  zusammenfassungImageSrc,
  page3ImageSrc,
  ratingsImageSrc,
  advisorsForPdf,
  fondspartnerLogoSrc,
  advisorFooterLogoSrc,
  fondsdatenCache,
  sectionHeaderCache,
  onPageLayout,
  onPage3ImageArea,
}) => {
  // Re-assert after SHV/PVM may have registered no-hyphenation globally.
  registerReactPdfGermanSafeWrap(14);

  const pdf = data || {};
  const template = templateProp || pdf.template;
  const { primary, secondary } = resolveSquadFsHeaderPalette({
    title: pdf.title || template?.fond,
    farbthemeFund: pdf.farbthemeFund,
    brandPrimary: pdf.brandPrimary || template?.color,
    brandSecondary: pdf.brandSecondary,
  });
  const chartPrimary = SQUAD_COLORS.chartPrimary;
  const portfolioBarColor =
    typeof pdf.portfolioBarColor === 'string' && pdf.portfolioBarColor.trim()
      ? pdf.portfolioBarColor.trim()
      : chartPrimary;
  // Must expand — sectionHeaders can be {isDynamic, fields} objects (React error #31 if rendered raw).
  const headers = expandSquadSectionHeaders(
    pdf.sectionHeaders,
    resolveSquadHeaderAsOf(pdf),
    sectionHeaderCache,
  );
  const title = pdf.title || template?.fond || 'SQUAD Value - A';
  /** Long fund name → shrink the title so it stays on one line (PowerPoint autofit analogue). */
  const titleFontSize = useMemo(() => fitSquadTitleFontSize(title), [title]);
  const styles = useMemo(
    () => getPdfStyles(primary, secondary, titleFontSize),
    [primary, secondary, titleFontSize],
  );
  const fondsdaten = resolveSquadFondsdatenRows(
    pdf.fondsdaten,
    squadLabeledKeys(SQUAD_FONDSDATEN_KEYS, pdf.fondsdatenLabels),
    fondsdatenCache,
  );
  // Already resolved by the bundle; resolving again keeps a dynamic row from ever printing
  // as "[object Object]" if the bundle did not arrive.
  const weitere = asKv(
    resolveSquadGebuehren(pdf.weitereDaten),
    squadLabeledKeys(SQUAD_WEITERE_DATEN_KEYS, pdf.weitereDatenLabels),
  );
  const partnerBlocks = buildSquadFondspartnerBlocks(pdf.fondspartner);
  const perf = performanceRiskTableForRender(pdf.performanceRiskTable);
  const mifidOverlay = normalizeMifidOverlay(pdf.mifidPerformanceOverlay);
  // No second default here — the mapper sets one. An unreadable level highlights no box.
  const riskLevel = parseSquadSrriLevel(pdf.riskIndicator) ?? 0;
  const navChart = chartImageDataUrl || pdf.chartImageDataUrl;
  const rollingChart =
    chart1ImageDataUrl || pdf.chart1ImageDataUrl || pdf.rollingPerformanceImageUrl;
  const pie1 = pieChart1ImageDataUrl || pdf.pieChart1ImageDataUrl;
  const pie2 = pieChart2ImageDataUrl || pdf.pieChart2ImageDataUrl;
  const historischeSrc =
    historischeAssetAllokationImageSrc || pdf.historischeAssetAllokationImageUrl;
  const investmentprozessSrc = investmentprozessImageSrc || pdf.investmentprozessImageUrl;
  const zusammenfassungSrc = zusammenfassungImageSrc || pdf.zusammenfassungImageUrl;
  const page3Layout = resolveSquadPage3Layout(pdf);
  const ratingsSrc = ratingsImageSrc || pdf.ratingsImageUrl;
  const advisorsPdf = advisorsForPdf || pdf.advisors;
  /**
   * Kein Rueckfall auf die eingebaute Marke: den Default liefert bereits der Mapper.
   * Ein zweiter Default hier wuerde nur feuern, wenn der Kunde das Logo bewusst
   * geleert hat — oder wenn sein Abruf fehlschlug, und dann gehoert dort eine Luecke
   * hin, nicht die Marke von jemand anderem.
   */
  const advisorLogoSrc = advisorFooterLogoSrc ?? '';

  const renderHeader = (withSidebarRail = false) => (
    <SquadPdfPageHeader
      title={title}
      metaLabel={squadPageMetaLabel(pdf)}
      styles={
        withSidebarRail
          ? { ...styles, pageHeader: styles.pageHeaderWithSidebar }
          : styles
      }
    />
  );

  const renderFixedHeader = () => (
    <View style={styles.pageHeaderFixed} fixed>
      {renderHeader(true)}
    </View>
  );

  /** Disclaimer page: same fixed header, just without the sidebar column. */
  const renderFixedHeaderFullWidth = () => (
    <View style={styles.pageHeaderFixed} fixed>
      {renderHeader()}
    </View>
  );

  /** Each sidebar page has its own notes, free text as typed — see squadFootnotes. */
  const renderSidebarBottom = (page: SquadSidebarFootnotePage, logoSrc?: string) => {
    const notes = parseSquadFootnotes(pdf[squadSidebarFootnoteKey(page)]);
    if (!notes.length && !logoSrc) return null;
    return (
      <View style={styles.sidebarBottomStack} fixed>
        {logoSrc ? (
          <View style={styles.sidebarBottomLogo}>
            <Image src={logoSrc} style={styles.sidebarBottomLogoImage} />
          </View>
        ) : null}
        {notes.length ? (
          <View style={styles.sidebarFootnote}>
            {notes.map((text, i) => (
              <Text key={i} style={styles.footnote}>
                {text}
              </Text>
            ))}
          </View>
        ) : null}
      </View>
    );
  };

  return (
    <Document>
      <Page size="A4" style={styles.pageWithSidebar}>
        <PageLayoutProbe page={1} onLayout={onPageLayout} />
        <View style={styles.pageSidebarRail} fixed />
        {renderFixedHeader()}
          <View style={[styles.mainCol, styles.mainColPage1]}>
            <View style={styles.mainSection}>
              <Text style={[styles.navyBar, styles.navyBarProse]}>{headers.anlagestrategie}</Text>
              <Text style={styles.proseText}>{pdf.anlagestrategie}</Text>
            </View>

            <View style={styles.mainSection}>
              <Text style={styles.navyBar}>{headers.wertentwicklungSeitBeratung}</Text>
              <MifidOverlayLayerPdf
                active={mifidOverlay.coverNavChart}
                noticeText={mifidOverlay.noticeText}
                minHeight={SQUAD_NAV_CHART_HEIGHT_PDF_PT}
              >
                {navChart ? (
                  <Image
                    src={navChart}
                    style={{ width: '100%', height: SQUAD_NAV_CHART_HEIGHT_PDF_PT }}
                  />
                ) : (
                  <Placeholder label="NAV chart (load preview first)" styles={styles} height={140} />
                )}
              </MifidOverlayLayerPdf>
              {pdf.chartFootnote ? (
                <FootnoteLines
                  text={pdf.chartFootnote}
                  style={{ fontSize: SQUAD_TYPOGRAPHY_PDF.note, color: SQUAD_COLORS.text, marginTop: 3 }}
                />
              ) : null}
            </View>

            <View style={styles.mainSection}>
              <Text style={styles.navyBar}>{headers.wertentwicklungRisiko}</Text>
              <MifidOverlayLayerPdf
                active={mifidOverlay.coverPerformanceRiskTable}
                noticeText={mifidOverlay.noticeText}
              >
                <PerformanceRiskTable perf={perf} styles={styles} />
              </MifidOverlayLayerPdf>
              {pdf.performanceRiskFootnote ? (
                <FootnoteLines
                  text={pdf.performanceRiskFootnote}
                  style={{ fontSize: SQUAD_TYPOGRAPHY_PDF.note, color: SQUAD_COLORS.text, marginTop: 3 }}
                />
              ) : null}
            </View>

            <View style={styles.mainSectionGrow}>
              <Text style={[styles.navyBar, styles.navyBarProse]}>{headers.monatskommentar}</Text>
              <InlineProseText text={pdf.monatskommentar} style={styles.proseText} />
            </View>

            <View style={styles.mainSectionFooter} wrap={false}>
              {ratingsSrc ? (
                <Placeholder label={IMAGE_SLOT_LABELS.ratingsSeals} styles={styles} height={47} src={ratingsSrc} />
              ) : null}
              {pdf.page1FooterNote ? (
                <FootnoteLines
                  text={pdf.page1FooterNote}
                  style={{ fontSize: SQUAD_TYPOGRAPHY_PDF.note, color: SQUAD_COLORS.text, marginTop: 4 }}
                />
              ) : null}
            </View>
          </View>
        <View style={styles.sidebar} wrap={false}>
            {fondsdaten.length > 0 ? (
            <View style={{ ...styles.section, marginBottom: 4 }}>
              <Text style={styles.goldBar}>{headers.fondsdaten}</Text>
              <KeyValueBlock rows={fondsdaten} styles={styles} />
            </View>
            ) : null}
            <View style={{ ...styles.section, marginBottom: 0 }}>
              <Text style={styles.goldBar}>{headers.fondsberater}</Text>
              <View style={{ marginTop: 2 }}>
                <SquadAdvisorsPdf
                  advisors={advisorsPdf}
                  styles={styles}
                  primary={SQUAD_COLORS.navy}
                  secondary={secondary}
                  footerLogoUrl={advisorLogoSrc}
                />
              </View>
            </View>
        </View>
        {renderSidebarBottom(1)}
      </Page>

      <Page size="A4" style={styles.pageWithSidebar}>
        <PageLayoutProbe page={2} onLayout={onPageLayout} />
        <View style={styles.pageSidebarRail} fixed />
        {renderFixedHeader()}
          <View style={styles.mainCol}>
            <SquadPortfolioSectionsPdf
              sections={Array.isArray(pdf.portfolioSections) ? pdf.portfolioSections : []}
              headers={headers}
              styles={styles}
              barColor={portfolioBarColor}
              chartPrimary={chartPrimary}
              rasterPies={isSquadRasterPie(pdf.pieChartStyle)}
              pieImages={[pie1, pie2]}
              pieStyle={pdf.pieChartStyle}
            />
          </View>
        <View style={styles.sidebar} wrap={false}>
            {weitere.length > 0 ? (
            <View style={styles.section}>
              <Text style={styles.goldBar}>{headers.weitereDaten}</Text>
              {/* Gebühren 55/45: „aktuell bis zu 1,50 %“ brach bei 60/40 um (Weekly 02.10.2026) */}
              <KeyValueBlock rows={weitere} styles={styles} labelWidthPct={SQUAD_GEBUEHREN_LABEL_WIDTH_PCT} />
            </View>
            ) : null}
            {partnerBlocks.length > 0 ? (
            <View style={styles.section}>
              <Text style={styles.goldBar}>{headers.fondspartner}</Text>
              <View style={{ marginTop: 8 }}>
                {partnerBlocks.map((block, i) => (
                  <View key={block.key} style={{ marginTop: i === 0 ? 0 : 8 }}>
                    <Text style={styles.partnerLine}>{`${block.label}:`}</Text>
                    {block.lines.map((line, j) => (
                      <Text key={j} style={styles.partnerLine}>
                        {line}
                      </Text>
                    ))}
                  </View>
                ))}
              </View>
            </View>
            ) : null}
            <View style={styles.section}>
              <Text style={styles.goldBar}>{headers.kontakt}</Text>
              <View style={{ marginTop: 8 }}>
                {[
                  { text: pdf.kontakt?.company, company: true },
                  { text: pdf.kontakt?.address },
                  { text: pdf.kontakt?.phone },
                  { text: pdf.kontakt?.email },
                  { text: pdf.kontakt?.website, website: true },
                ]
                  .filter((line) => String(line.text ?? '').trim())
                  .map((line, i) => (
                    <Text
                      key={i}
                      style={{
                        fontSize: line.company
                          ? SQUAD_TYPOGRAPHY_PDF.kontaktCompany
                          : SQUAD_TYPOGRAPHY_PDF.kontakt,
                        fontFamily: line.company || line.website ? 'SegoeUI' : undefined,
                        fontWeight: line.company || line.website ? 700 : undefined,
                      }}
                    >
                      {line.text}
                    </Text>
                  ))}
              </View>
            </View>
        </View>
        {renderSidebarBottom(2, fondspartnerLogoSrc)}
      </Page>

      <Page size="A4" style={styles.pageWithSidebar}>
        <PageLayoutProbe page={3} onLayout={onPageLayout} />
        <View style={styles.pageSidebarRail} fixed />
        {renderFixedHeader()}
          <View style={styles.mainCol}>
            <View style={styles.mainSection}>
              <Text style={styles.navyBar}>{headers.kalenderjahr}</Text>
              <MifidOverlayLayerPdf
                active={mifidOverlay.coverKalenderjahr}
                noticeText={mifidOverlay.noticeText}
              >
                <KalenderjahrTable data={pdf.kalenderjahrPerformance} styles={styles} />
              </MifidOverlayLayerPdf>
              {pdf.kalenderjahrFootnote ? (
                <FootnoteLines
                  text={pdf.kalenderjahrFootnote}
                  style={{ fontSize: SQUAD_TYPOGRAPHY_PDF.note, color: SQUAD_COLORS.text, marginTop: 3 }}
                />
              ) : null}
            </View>
            <View style={styles.mainSection} wrap={false}>
              <Text style={styles.navyBar}>{headers.rollierend}</Text>
              <MifidOverlayLayerPdf
                active={mifidOverlay.coverRollingPerformance}
                noticeText={mifidOverlay.noticeText}
                minHeight={SQUAD_ROLLING_CHART_HEIGHT_PDF_PT}
              >
                {rollingChart ? (
                  <Image
                    src={rollingChart}
                    style={{ width: '100%', height: SQUAD_ROLLING_CHART_HEIGHT_PDF_PT, marginTop: 4 }}
                  />
                ) : (
                  <Placeholder
                    label={IMAGE_SLOT_LABELS.rollingPerformance}
                    styles={styles}
                    height={100}
                  />
                )}
              </MifidOverlayLayerPdf>
              {pdf.rollingPerformanceFootnote ? (
                <FootnoteLines
                  text={pdf.rollingPerformanceFootnote}
                  style={{ fontSize: SQUAD_TYPOGRAPHY_PDF.note, color: SQUAD_COLORS.text, marginTop: 3 }}
                />
              ) : null}
            </View>
            {page3Layout.kind === 'legacy' ? (
              <>
                <View style={styles.mainSection} wrap={false}>
                  <Text style={styles.navyBar}>{headers.historischeAssetAllokation}</Text>
                  <Placeholder
                    label={IMAGE_SLOT_LABELS.historischeAssetAllokation}
                    styles={styles}
                    height={130}
                    src={historischeSrc}
                  />
                </View>
                <View style={styles.mainSection} wrap={false}>
                  <Text style={styles.navyBar}>{headers.investmentprozess}</Text>
                  <Placeholder
                    label={IMAGE_SLOT_LABELS.investmentprozess}
                    styles={styles}
                    height={130}
                    src={investmentprozessSrc}
                  />
                </View>
                <View style={styles.mainSection} wrap={false}>
                  <Text style={styles.navyBar}>{headers.zusammenfassung}</Text>
                  <Placeholder
                    label={IMAGE_SLOT_LABELS.zusammenfassung}
                    styles={styles}
                    height={90}
                    src={zusammenfassungSrc}
                    preserveAspectRatio
                  />
                </View>
              </>
            ) : (
              <SquadPage3ImagePdf
                src={page3ImageSrc || page3Layout.imageUrl}
                onArea={onPage3ImageArea}
              />
            )}
            {pdf.page3FooterNote ? (
              // Anchored at the foot of the column like page 1, instead of riding up under the last image
              <View style={styles.mainSectionFooter} wrap={false}>
                <FootnoteLines
                  text={pdf.page3FooterNote}
                  style={{
                    fontSize: SQUAD_TYPOGRAPHY_PDF.note,
                    color: SQUAD_COLORS.text,
                    marginTop: 4,
                  }}
                />
              </View>
            ) : null}
          </View>
        <View style={styles.sidebar} wrap={false}>
            <View style={styles.section}>
              <Text style={styles.goldBar}>{headers.chancen}</Text>
              <View style={{ marginTop: 8 }}>
                <BulletList text={pdf.chancen} marker="+" styles={styles} primary={SQUAD_COLORS.navy} />
              </View>
            </View>
            <View style={styles.section}>
              <Text style={styles.goldBar}>{headers.risiken}</Text>
              <View style={{ marginTop: 8 }}>
                <BulletList text={pdf.risiken} marker="-" styles={styles} primary={SQUAD_COLORS.navy} />
                {pdf.risikenNote ? (
                  <Text style={styles.risikenNote}>{pdf.risikenNote}</Text>
                ) : null}
              </View>
            </View>
            <View style={styles.section}>
              <Text style={styles.goldBar}>{headers.risikoIndikator}</Text>
              <View style={{ marginTop: 8 }}>
                <RiskIndicatorBlock
                  level={riskLevel}
                  text={pdf.riskIndicatorText}
                  styles={styles}
                  primary={SQUAD_COLORS.navy}
                  activeFill={primary}
                />
              </View>
            </View>
        </View>
        {renderSidebarBottom(3)}
      </Page>

      <Page size="A4" style={styles.pageFullWithFixedHeader}>
        <PageLayoutProbe page={4} onLayout={onPageLayout} />
        {renderFixedHeaderFullWidth()}
        <View style={styles.page4}>
          <View style={styles.mainSection}>
            <Text style={styles.navyBar}>{headers.erlaeuterungen}</Text>
            <RichHtmlPdf html={String(pdf.erlaeuterungen || '')} style={styles.disclaimerText} />
          </View>
          <View style={styles.mainSection}>
            <Text style={styles.navyBar}>{headers.wichtigeHinweise}</Text>
            <RichHtmlPdf html={String(pdf.wichtigeHinweise || '')} style={styles.disclaimerText} />
          </View>
        </View>
      </Page>
    </Document>
  );
};

interface PdfPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  data: any;
  template: any;
  title?: string;
}

const dialogStyles = {
  dialog: {
    '& .MuiDialog-paper': {
      width: '90vw',
      height: '90vh',
      maxWidth: '1200px',
      maxHeight: '900px',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
  },
  dialogTitle: {
    borderBottom: '1px solid #e5e7eb',
    padding: '16px 24px',
    flexShrink: 0,
    '& .MuiTypography-root': {
      fontSize: '18px',
      fontWeight: 600,
    },
  },
  dialogContent: {
    padding: '0 !important',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  pdfContainer: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    background: '#525659',
  },
  loadingState: {
    height: '100%',
    minHeight: 280,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
};

export const PdfPreviewDialog: FC<PdfPreviewDialogProps> = ({
  open,
  onClose,
  data,
  template,
  title = 'PDF Preview',
}) => {
  const [historischeSrc, setHistorischeSrc] = useState<string | undefined>();
  const [investmentprozessSrc, setInvestmentprozessSrc] = useState<string | undefined>();
  const [zusammenfassungSrc, setZusammenfassungSrc] = useState<string | undefined>();
  const [page3Src, setPage3Src] = useState<string | undefined>();
  const [page3ImageSize, setPage3ImageSize] = useState<SquadImageSize | null>(null);
  const [page3Area, setPage3Area] = useState<SquadPage3ImageArea | null>(null);
  const [ratingsSrc, setRatingsSrc] = useState<string | undefined>();
  const [advisorsForPdf, setAdvisorsForPdf] = useState<SquadAdvisorRecord[]>([]);
  const [advisorFooterLogoSrc, setAdvisorFooterLogoSrc] = useState<string | undefined>();
  const [fondspartnerLogoSrc, setFondspartnerLogoSrc] = useState('');
  const [slotImagesReady, setSlotImagesReady] = useState(false);
  const [resolvedRiskIndicator, setResolvedRiskIndicator] = useState<number | null>(null);
  const [fondsdatenCache, setFondsdatenCache] = useState<Map<string, string>>(new Map());
  const [sectionHeaderCache, setSectionHeaderCache] = useState<Map<string, string>>(new Map());
  const [dynamicBundle, setDynamicBundle] = useState<SquadDynamicBundle | null>(null);
  const [pageLayout, setPageLayout] = useState<SquadPageLayout | null>(null);
  const pageLayoutRef = useRef<{ spans: Map<number, Set<number>>; total: number }>({ spans: new Map(), total: 0 });
  const pageLayoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Sonden melden während des react-pdf-Layouts; gesammelt und kurz danach ausgewertet. */
  const handlePageLayout = useCallback((logicalPage: number, pageNumber: number, totalPages: number) => {
    const current = pageLayoutRef.current;
    if (current.total !== totalPages) {
      current.spans = new Map();
      current.total = totalPages;
    }
    const physical = current.spans.get(logicalPage) || new Set<number>();
    physical.add(pageNumber);
    current.spans.set(logicalPage, physical);
    if (pageLayoutTimerRef.current) clearTimeout(pageLayoutTimerRef.current);
    pageLayoutTimerRef.current = setTimeout(() => {
      pageLayoutTimerRef.current = null;
      const next = summarizeSquadPageLayout(current.spans, current.total);
      // Nur bei echter Änderung — sonst rendert der Dialog neu und mit ihm das PDF.
      setPageLayout((prev) => (prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    }, 50);
  }, []);
  useEffect(
    () => () => {
      if (pageLayoutTimerRef.current) clearTimeout(pageLayoutTimerRef.current);
    },
    [],
  );
  /** Reported while react-pdf draws page 3; only a real change re-renders the dialog. */
  const handlePage3ImageArea = useCallback((widthPt: number, heightPt: number) => {
    // A height of 0 is a real answer ("no room left"); no width or a NaN means no layout.
    if (!Number.isFinite(widthPt) || !Number.isFinite(heightPt) || widthPt <= 0 || heightPt < 0) return;
    setPage3Area((prev) =>
      prev && prev.widthPt === widthPt && prev.heightPt === heightPt ? prev : { widthPt, heightPt },
    );
  }, []);

  useEffect(() => {
    if (!open) {
      setSlotImagesReady(false);
      setResolvedRiskIndicator(null);
      setFondsdatenCache(new Map());
      setSectionHeaderCache(new Map());
      setDynamicBundle(null);
      return;
    }
    let cancelled = false;
    setSlotImagesReady(false);

    const load = async () => {
      const advisorsIn = normalizeSquadAdvisors(data?.advisors);
      const [
        historische,
        investmentprozess,
        zusammenfassung,
        page3,
        ratings,
        footerLogo,
        fondspartnerLogo,
        resolvedAdvisors,
        bundle,
      ] = await Promise.all([
        resolveSlotImageForPdf(data?.historischeAssetAllokationImageUrl),
        resolveSlotImageForPdf(data?.investmentprozessImageUrl),
        resolveSlotImageForPdf(data?.zusammenfassungImageUrl),
        // Measured on what the PDF embeds; an image only react-pdf can fetch is measured by its URL.
        resolveSlotImageForPdf(data?.page3ImageUrl).then(async (src) => ({
          src,
          size: await loadSquadImageSize(src || data?.page3ImageUrl),
        })),
        resolveSlotImageForPdf(data?.ratingsImageUrl),
        resolveSlotImageForPdf(data?.advisorLogoImageUrl),
        resolveSlotImageForPdf(data?.fondspartnerLogoImageUrl),
        Promise.all(
          advisorsIn.map(async (a) => ({
            ...a,
            photoUrl: await resolveSlotImageForPdf(a.photoUrl),
            logoUrl: await resolveSlotImageForPdf(a.logoUrl),
          })),
        ),
        fetchSquadDynamicBundle(data, template),
      ]);
      if (cancelled) return;
      setHistorischeSrc(historische);
      setInvestmentprozessSrc(investmentprozess);
      setZusammenfassungSrc(zusammenfassung);
      setPage3Src(page3.src);
      setPage3ImageSize(page3.size);
      setRatingsSrc(ratings);
      setAdvisorFooterLogoSrc(footerLogo);
      setFondspartnerLogoSrc(fondspartnerLogo || '');
      // Must be ready before the first render, else the title autofit measures with a fallback font
      await ensureSquadTitleMeasureFont();
      if (cancelled) return;
      setAdvisorsForPdf(resolvedAdvisors);
      setResolvedRiskIndicator(bundle.srri);
      setFondsdatenCache(bundle.fondsdatenCache);
      setSectionHeaderCache(bundle.sectionHeaderCache);
      setDynamicBundle(bundle);
      setSlotImagesReady(true);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    data?.historischeAssetAllokationImageUrl,
    data?.investmentprozessImageUrl,
    data?.zusammenfassungImageUrl,
    data?.page3ImageUrl,
    data?.ratingsImageUrl,
    data?.advisors,
    data?.advisorLogoImageUrl,
    data?.fondspartnerLogoImageUrl,
    data?.fondsdaten,
    data?.sectionHeaders,
    data?.performanceRiskTable,
    data?.weitereDaten,
    data?.historyIsin,
    data?.fondsdaten?.isin,
    data?.published_at,
    data?.historyUntil,
    template,
  ]);

  // Rendered with the fetched values: SRRI as of the factsheet date, table cells resolved.
  const pdfData = useMemo(() => {
    if (!dynamicBundle) return data;
    return {
      ...data,
      ...(resolvedRiskIndicator != null ? { riskIndicator: resolvedRiskIndicator } : {}),
      performanceRiskTable: dynamicBundle.performanceRiskTable,
      weitereDaten: dynamicBundle.weitereDaten,
    };
  }, [data, resolvedRiskIndicator, dynamicBundle]);


  const downloadFileName = useMemo(
    () =>
      buildFactsheetDownloadFileName({
        customTemplate: data?.pdf_download_name,
        title: data?.title || template?.fond || template?.name || 'Factsheet',
        asOf: resolveFactsheetDownloadAsOf(data),
      }),
    [data, template?.fond, template?.name],
  );

  // Memoisiert: die Überlauf-Warnung (State im Dialog) darf das PDF nicht neu auslösen.
  const factsheetPdf = useMemo(
    () => (
    <FactsheetPDF
      data={pdfData}
      template={template}
      chartImageDataUrl={data?.chartImageDataUrl}
      chart1ImageDataUrl={data?.chart1ImageDataUrl}
      pieChart1ImageDataUrl={data?.pieChart1ImageDataUrl}
      pieChart2ImageDataUrl={data?.pieChart2ImageDataUrl}
      historischeAssetAllokationImageSrc={historischeSrc}
      investmentprozessImageSrc={investmentprozessSrc}
      zusammenfassungImageSrc={zusammenfassungSrc}
      page3ImageSrc={page3Src}
      ratingsImageSrc={ratingsSrc}
      advisorsForPdf={advisorsForPdf}
      advisorFooterLogoSrc={advisorFooterLogoSrc}
      fondspartnerLogoSrc={fondspartnerLogoSrc}
      fondsdatenCache={fondsdatenCache}
      sectionHeaderCache={sectionHeaderCache}
      onPageLayout={handlePageLayout}
      onPage3ImageArea={handlePage3ImageArea}
    />
    ),
    [
      pdfData,
      template,
      data?.chartImageDataUrl,
      data?.chart1ImageDataUrl,
      data?.pieChart1ImageDataUrl,
      data?.pieChart2ImageDataUrl,
      historischeSrc,
      investmentprozessSrc,
      zusammenfassungSrc,
      page3Src,
      ratingsSrc,
      advisorsForPdf,
      advisorFooterLogoSrc,
      fondspartnerLogoSrc,
      fondsdatenCache,
      sectionHeaderCache,
      handlePageLayout,
      handlePage3ImageArea,
    ],
  );

  // Jedes neu gebaute PDF (auch nur ein spätes Bild) wird neu vermessen — sonst vereinigten
  // sich bei gleicher Seitenzahl alte und neue Spannen zu einer veralteten Warnung.
  useEffect(() => {
    pageLayoutRef.current = { spans: new Map(), total: 0 };
    setPage3Area(null);
  }, [factsheetPdf]);
  useEffect(() => {
    if (!open) {
      setPageLayout(null);
      setPage3Area(null);
      setPage3ImageSize(null);
    }
  }, [open]);
  const pageLayoutWarning = squadPageLayoutWarning(pageLayout);
  const missingDynamicWarning = squadMissingDynamicWarning(dynamicBundle);
  // Only once the images are in: a size from the previous image must not warn about the next one.
  const page3ImageWarning =
    slotImagesReady && resolveSquadPage3Layout(data).kind === 'single'
      ? squadPage3ImageWarning(page3Area, page3ImageSize)
      : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} sx={dialogStyles.dialog}>
      <DialogTitle sx={dialogStyles.dialogTitle}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <Box>{title}</Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={dialogStyles.dialogContent}>
        {missingDynamicWarning ? (
          <Alert severity="warning" sx={{ borderRadius: 0 }}>
            {missingDynamicWarning}
          </Alert>
        ) : null}
        {pageLayoutWarning ? (
          <Alert severity="warning" sx={{ borderRadius: 0 }}>
            {pageLayoutWarning}
          </Alert>
        ) : null}
        {page3ImageWarning ? (
          <Alert severity={page3ImageWarning.severity} sx={{ borderRadius: 0 }}>
            {page3ImageWarning.text}
          </Alert>
        ) : null}
        <Box sx={dialogStyles.pdfContainer}>
          {open && slotImagesReady ? (
            <PdfPreviewWithTools document={factsheetPdf} fileName={downloadFileName} />
          ) : open ? (
            <Box sx={dialogStyles.loadingState} role="status" aria-live="polite">
              <CircularProgress size={40} thickness={4} sx={{ color: '#ffffff' }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, fontWeight: 500 }}>
                Preparing preview…
              </Typography>
            </Box>
          ) : null}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
